import { useEffect } from 'react';

import { useTurnstile } from '@/views/Auth/hooks';

import type { TTurnstileBridgeProps } from './types';

/**
 * Headless bridge between `useTurnstile()` and a form component.
 *
 * Mounting this component IS what starts loading Cloudflare Turnstile and
 * rendering the invisible widget. CORE-1795 ships without a feature flag (the
 * kill switch is BE-side only — `TURNSTILE_SECRET_KEY` empty ⇒ inert, fail-open —
 * plus deploy rollback), so every recovery/reset form mounts this unconditionally.
 * It stays a separate component rather than calling `useTurnstile()` directly in
 * each form: it keeps the (fairly involved) widget-lifecycle hook decoupled from
 * each form's own render/hook tree, and gives every consumer the exact same
 * `{ token, status, reset }` contract via `onChange` with zero duplication.
 */
export const TurnstileBridge = ({ onChange }: TTurnstileBridgeProps) => {
  const { token, status, reset } = useTurnstile();

  // biome-ignore lint/correctness/useExhaustiveDependencies: onChange is expected to be a stable setter from the parent's useState
  useEffect(() => {
    onChange({ token, status, reset });
  }, [token, status, reset]);

  return null;
};
